import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { filesApi } from '@/lib/api';
import { formatFileSize } from '@/constants/utils/files';
import Dropzone from './Dropzone';
import {
  describeFileLimitViolation,
  probeMediaDurationSeconds,
} from '@/constants/utils/file_upload_limits';

// Procedurally-generated bar heights so the empty preview still looks
// like a waveform instead of a flatline. Stable across renders.
const PLACEHOLDER_BARS = Array.from({ length: 48 }, (_, i) =>
  20 + Math.round(Math.abs(Math.sin(i * 0.7) * Math.cos(i * 0.31)) * 24 + (i % 5) * 2),
);

function pad2(n) { return String(n).padStart(2, '0'); }
function fmtTime(sec) {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${pad2(s)}`;
}

function AudioWaveform({
  value,
  onChange,
  disabled,
  accept = 'audio/*',
  label = 'Choose audio',
  limitsHint,
  maxSizeBytes,
  maxDurationSeconds,
}) {
  const { colors } = useTheme();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0); // 0..1

  const audioRef = useRef(null);
  const trackRef = useRef(null);

  const fileId = typeof value === 'string' ? value : (value?.id || value?._id || value?.audioId || null);
  const meta = value && typeof value === 'object' ? value : null;
  const audioUrl = meta?.cdnPath || meta?.url || null;
  // A placeholder preset has a cdnPath but no real backend file id. We still
  // render the player so the user can preview the example audio; a small
  // "SAMPLE" badge tells them they need to upload their own to actually run.
  const isSample = !!(meta && !fileId && audioUrl);
  const fileName = meta?.name || (fileId ? 'Uploaded audio' : (isSample ? 'Sample audio' : null));

  const styles = useMemo(() => StyleSheet.create({
    wrap: { gap: 12 },
    player: {
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      borderRadius: 14,
      backgroundColor: colors.solid_color_tertiary,
      padding: 14,
      gap: 12,
      overflow: 'hidden',
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    playButton: {
      width: 44,
      height: 44,
      borderRadius: 999,
      // Solid neutral so the button reads against the panel background.
      backgroundColor: colors.text_color_primary,
      alignItems: 'center',
      justifyContent: 'center',
      ...(Platform.OS === 'web' && { transition: 'transform 120ms ease' }),
    },
    playButtonHover: {
      ...(Platform.OS === 'web' && { transform: [{ scale: 1.05 }] }),
    },
    headerMeta: { flex: 1, minWidth: 0, gap: 2 },
    metaRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    removeBtn: {
      width: 32,
      height: 32,
      borderRadius: 999,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
    },
    waveformWrap: { gap: 6 },
    waveform: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: 56,
      gap: 2,
      ...(Platform.OS === 'web' && { cursor: audioUrl ? 'pointer' : 'default' }),
    },
    bar: {
      flex: 1,
      borderRadius: 999,
      backgroundColor: colors.text_color_tertiary,
    },
    barPlayed: {
      backgroundColor: colors.text_color_primary,
    },
    timesRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  }), [colors, audioUrl]);

  // ---------------------------------------------------------------------------
  // Playback wiring (web only — RN's expo-av API is being deprecated and the
  // user has no specific native playback need yet; the file is still uploaded).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    setIsPlaying(false);
    setProgress(0);
    setDuration(0);
  }, [audioUrl]);

  const handlePlayPause = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => setError('Could not play audio'));
    else a.pause();
  }, []);

  const handleSeekFromBar = useCallback((event) => {
    const a = audioRef.current;
    const t = trackRef.current;
    if (!a || !t || !duration) return;
    const rect = t.getBoundingClientRect?.();
    if (!rect) return;
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    a.currentTime = ratio * duration;
    setProgress(ratio);
  }, [duration]);

  // ---------------------------------------------------------------------------
  // Upload
  // ---------------------------------------------------------------------------
  const uploadFile = useCallback(async (files) => {
    const file = Array.isArray(files) ? files[0] : files;
    if (!file) return;
    setError(null);
    const probedDuration = await probeMediaDurationSeconds(file);
    const limitError = describeFileLimitViolation(
      { size: file.size, duration: probedDuration },
      { maxSizeBytes, maxDurationSeconds },
    );
    if (limitError) {
      setError(limitError);
      return;
    }
    setIsUploading(true);
    try {
      const { file: saved } = await filesApi.uploadFile({ file });
      if (!saved?.id) throw new Error('Upload failed');
      onChange?.({
        id: saved.id,
        audioId: saved.id,
        name: saved.name || saved.originalName || file.name,
        mimeType: saved.mimeType || file.type,
        sizeInBytes: saved.sizeInBytes ?? saved.sizeBytes ?? file.size,
        cdnPath: saved.cdnPath || null,
        thumbnailPath: saved.thumbnailPath || null,
        fileType: saved.fileType,
        metadata: saved.metadata || null,
      });
    } catch (e) {
      setError(e?.message || 'Failed to upload audio');
    } finally {
      setIsUploading(false);
    }
  }, [onChange, maxSizeBytes, maxDurationSeconds]);

  const handleLibrary = useCallback((picked) => {
    const saved = picked?.[0];
    if (!saved?.id) return;
    const limitError = describeFileLimitViolation(saved, { maxSizeBytes, maxDurationSeconds });
    if (limitError) {
      setError(limitError);
      return;
    }
    setError(null);
    onChange?.({
      id: saved.id,
      audioId: saved.id,
      name: saved.name || saved.originalName,
      mimeType: saved.mimeType,
      sizeInBytes: saved.sizeInBytes ?? saved.sizeBytes,
      cdnPath: saved.cdnPath || null,
      thumbnailPath: saved.thumbnailPath || null,
      fileType: saved.fileType,
      metadata: saved.metadata || null,
    });
  }, [onChange, maxSizeBytes, maxDurationSeconds]);

  const clearFile = useCallback(() => {
    onChange?.(null);
    setIsPlaying(false);
    setProgress(0);
  }, [onChange]);

  // ---------------------------------------------------------------------------
  // Render: dropzone when empty, full player when uploaded.
  // ---------------------------------------------------------------------------
  if (!fileId && !isSample) {
    return (
      <View style={styles.wrap}>
        <Dropzone
          accept={accept}
          disabled={disabled}
          isUploading={isUploading}
          icon='waveform'
          primaryLabel={label || 'Drag and drop an audio file'}
          secondaryLabel={limitsHint ? `MP3, WAV, OGG, M4A · ${limitsHint}` : 'MP3, WAV, OGG, M4A'}
          onFiles={uploadFile}
          onLibrary={handleLibrary}
        />
        {!!error && <Text size='s' color='danger'>{error}</Text>}
      </View>
    );
  }

  const playedIndex = Math.floor(progress * PLACEHOLDER_BARS.length);

  return (
    <View style={styles.wrap}>
      <View style={styles.player}>
        <View style={styles.headerRow}>
          <Pressable
            onPress={handlePlayPause}
            disabled={!audioUrl}
            style={({ hovered }) => [styles.playButton, hovered && styles.playButtonHover, !audioUrl && { opacity: 0.6 }]}
          >
            <Icon name={isPlaying ? 'pause' : 'play'} size={16} color='#ffffff' />
          </Pressable>

          <View style={styles.headerMeta}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <Text size='s' weight='bold' numberOfLines={1} style={{ flexShrink: 1 }}>{fileName}</Text>
              {isSample && (
                <View style={{
                  flexDirection: 'row', alignItems: 'center', gap: 3,
                  paddingHorizontal: 6, paddingVertical: 2, borderRadius: 999,
                  backgroundColor: colors.solid_color_block,
                  borderWidth: 1, borderColor: colors.border_color_secondary,
                }}>
                  <Icon name='sparkles' size={8} color={colors.text_color_secondary} />
                  <Text size='xxs' weight='bold' color='secondary'>SAMPLE</Text>
                </View>
              )}
            </View>
            <View style={styles.metaRow}>
              <Icon name='waveform' size={11} color={colors.text_color_tertiary} />
              <Text size='xs' color='tertiary' numberOfLines={1}>
                {meta?.sizeInBytes ? formatFileSize(meta.sizeInBytes) : 'Audio file'}
                {duration > 0 ? `  ·  ${fmtTime(duration)}` : ''}
              </Text>
            </View>
          </View>

          {!disabled && (
            <Pressable onPress={clearFile} style={styles.removeBtn} hitSlop={6}>
              <Icon name='xmark' size={12} color={colors.text_color_secondary} />
            </Pressable>
          )}
        </View>

        <View style={styles.waveformWrap}>
          {Platform.OS === 'web' ? (
            <div
              ref={trackRef}
              onClick={handleSeekFromBar}
              style={{ display: 'flex', alignItems: 'center', gap: 2, height: 56, cursor: audioUrl ? 'pointer' : 'default' }}
            >
              {PLACEHOLDER_BARS.map((h, i) => (
                <View
                  key={i}
                  style={[
                    styles.bar,
                    { height: h },
                    i <= playedIndex && styles.barPlayed,
                  ]}
                />
              ))}
            </div>
          ) : (
            <View style={styles.waveform}>
              {PLACEHOLDER_BARS.map((h, i) => (
                <View
                  key={i}
                  style={[
                    styles.bar,
                    { height: h },
                    i <= playedIndex && styles.barPlayed,
                  ]}
                />
              ))}
            </View>
          )}

          {duration > 0 && (
            <View style={styles.timesRow}>
              <Text size='xs' color='tertiary'>{fmtTime(progress * duration)}</Text>
              <Text size='xs' color='tertiary'>{fmtTime(duration)}</Text>
            </View>
          )}
        </View>

        {Platform.OS === 'web' && audioUrl && (
          // eslint-disable-next-line react/forbid-elements
          <audio
            ref={audioRef}
            src={audioUrl}
            preload='metadata'
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
            onTimeUpdate={(e) => {
              const a = e.currentTarget;
              if (!a.duration) return;
              setProgress(a.currentTime / a.duration);
            }}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => { setIsPlaying(false); setProgress(0); }}
            style={{ display: 'none' }}
          />
        )}
      </View>

      {!!error && <Text size='s' color='danger'>{error}</Text>}
    </View>
  );
}

export default React.memo(AudioWaveform);
